import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAuthStore } from './authStore'
import { cookieService } from '@/services/cookieService'

export const useCookieStore = defineStore('cookie', () => {
  const POLICY_VERSION = import.meta.env.VITE_COOKIE_POLICY_VERSION || 'v1.0.0'
  
  const preferences = ref({
    essential: true,
    analytics: false,
    marketing: false
  })
  const isAccepted = ref(false)
  const showBanner = ref(true)
  const showSettings = ref(false)
  const consentId = ref(null)

  const generateConsentId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0
      const v = c === 'x' ? r : (r & 0x3 | 0x8)
      return v.toString(16)
    })
  }

  const loadPreferences = () => {
    const storedPrefs = localStorage.getItem('cookie-preferences')
    const storedId = localStorage.getItem('consent-id')

    if (storedPrefs) {
      try {
        preferences.value = JSON.parse(storedPrefs)
        isAccepted.value = true
        showBanner.value = false
      } catch (e) {
        console.error('Failed to parse cookie preferences', e)
      }
    }

    if (storedId) {
      consentId.value = storedId
    } else {
      consentId.value = generateConsentId()
      localStorage.setItem('consent-id', consentId.value)
    }

    // Sync consent ID to technical cookie for audit compliance
    cookieService.setConsentCookie(consentId.value)
  }

  const savePreferences = (newPrefs, action = 'save_custom') => {
    preferences.value = { ...preferences.value, ...newPrefs, essential: true }
    isAccepted.value = true
    showBanner.value = false
    showSettings.value = false
    localStorage.setItem('cookie-preferences', JSON.stringify(preferences.value))
    
    // Sync consent ID to technical cookie
    cookieService.setConsentCookie(consentId.value)

    // Update scripts based on new preferences
    cookieService.clearScripts()
    cookieService.loadScripts(preferences.value)
    
    logConsent(action)
  }

  const acceptAll = () => {
    savePreferences({ analytics: true, marketing: true }, 'ACCEPT_ALL')
  }

  const rejectAll = () => {
    savePreferences({ analytics: false, marketing: false }, 'REJECT_ALL')
  }

  const logConsent = async (action) => {
    const payload = {
      timestamp: new Date().toISOString(),
      consentId: consentId.value,
      action: action,
      policyVersion: POLICY_VERSION,
      originUrl: window.location.href,
      culture: navigator.language || 'it-IT',
      preferences: preferences.value,
      userAgent: navigator.userAgent
    }

    const headers = {
      'Content-Type': 'application/json'
    }

    // Add auth token if present for backend to enrich logs
    const token = sessionStorage.getItem('authToken') || sessionStorage.getItem('token') || localStorage.getItem('token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    try {
      await fetch(`${import.meta.env.VITE_API_BASE_URL}/consent-log`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      })
    } catch (err) {
      console.error('[COOKIE_STORE] Failed to log consent to backend:', err)
    }
  }

  const openSettings = () => {
    showSettings.value = true
  }

  return {
    preferences,
    isAccepted,
    showBanner,
    showSettings,
    consentId,
    loadPreferences,
    savePreferences,
    acceptAll,
    rejectAll,
    logConsent,
    openSettings
  }
})
